import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Home from '../Home';
import { Loading } from '../../components/Loading';
import { useAnalyticsDashboard } from '../../utils/Hooks/useAnalyticsDashboard';
import styles from '../../utils/styles/pages/HeatmapViewer/style.module.css';

const DEVICE_PRESETS = {
  desktop: { label: 'Desktop' },
  mobile: { label: 'Mobile' }
};
const HEAT_STOPS = [
  { ratio: 0, rgb: [34, 86, 255] },
  { ratio: 0.2, rgb: [0, 177, 255] },
  { ratio: 0.45, rgb: [77, 226, 170] },
  { ratio: 0.68, rgb: [255, 223, 97] },
  { ratio: 0.86, rgb: [255, 112, 44] },
  { ratio: 1, rgb: [255, 22, 22] }
];

function interpolateChannel(start, end, progress) {
  return Math.round(start + ((end - start) * progress));
}

function getHeatRgb(ratio) {
  const safeRatio = Math.min(1, Math.max(0, ratio));

  for (let index = 1; index < HEAT_STOPS.length; index += 1) {
    const currentStop = HEAT_STOPS[index];
    const previousStop = HEAT_STOPS[index - 1];

    if (safeRatio <= currentStop.ratio) {
      const localProgress = (safeRatio - previousStop.ratio) / (currentStop.ratio - previousStop.ratio || 1);

      return previousStop.rgb.map((channel, channelIndex) => (
        interpolateChannel(channel, currentStop.rgb[channelIndex], localProgress)
      ));
    }
  }

  return HEAT_STOPS[HEAT_STOPS.length - 1].rgb;
}

function getAvailableDevices(heatmaps) {
  return ['desktop', 'mobile'].filter((deviceKey) => {
    const deviceHeatmaps = heatmaps?.[deviceKey];

    return ['mouse', 'touch', 'attention'].some((modeKey) => (
      (deviceHeatmaps?.[modeKey]?.totalSamples || 0) > 0
    ));
  });
}

function getPreferredMode(heatmapGroup) {
  if ((heatmapGroup?.mouse?.totalSamples || 0) > 0) {
    return 'mouse';
  }

  if ((heatmapGroup?.touch?.totalSamples || 0) > 0) {
    return 'touch';
  }

  return 'attention';
}

function getModeLabel(modeKey) {
  if (modeKey === 'mouse') {
    return 'Souris';
  }

  if (modeKey === 'touch') {
    return 'Taps';
  }

  return 'Attention';
}

function HeatCanvas({ heatmap, viewport, width, height }) {
  const canvasRef = useRef(null);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas || !heatmap || width <= 0 || height <= 0) {
      return undefined;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const context = canvas.getContext('2d');

    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, width, height);
    context.globalCompositeOperation = 'lighter';

    const significanceFloor = heatmap.significanceFloor || 0;
    const filledCells = [...(heatmap.cells || [])]
      .filter((cell) => cell.value >= significanceFloor)
      .sort((left, right) => left.value - right.value);
    const viewportWidth = Number.isFinite(viewport?.width) && viewport.width > 0 ? viewport.width : width;
    const viewportHeight = Number.isFinite(viewport?.height) && viewport.height > 0
      ? Math.min(viewport.height, height)
      : Math.min(height, viewportWidth);
    const horizontalStep = heatmap.columns > 1 ? viewportWidth / (heatmap.columns - 1) : viewportWidth;
    const verticalStep = heatmap.rows > 1 ? viewportHeight / (heatmap.rows - 1) : viewportHeight;

    filledCells.forEach((cell) => {
      const intensity = heatmap.maxValue > 0 ? cell.value / heatmap.maxValue : 0;
      const [red, green, blue] = getHeatRgb(intensity);
      const centerX = heatmap.columns > 1
        ? (cell.column / (heatmap.columns - 1)) * width
        : width / 2;
      const centerY = heatmap.rows > 1
        ? (cell.row / (heatmap.rows - 1)) * height
        : height / 2;
      const radiusX = horizontalStep * (1.1 + (intensity * 1.65));
      const radiusY = verticalStep * (0.95 + (intensity * 1.5));

      context.save();
      context.translate(centerX, centerY);
      context.scale(radiusX, radiusY);
      const gradient = context.createRadialGradient(0, 0, 0, 0, 0, 1);

      gradient.addColorStop(0, `rgba(${red}, ${green}, ${blue}, ${0.22 + (intensity * 0.58)})`);
      gradient.addColorStop(0.28, `rgba(${red}, ${green}, ${blue}, ${0.16 + (intensity * 0.34)})`);
      gradient.addColorStop(0.62, `rgba(${red}, ${green}, ${blue}, ${0.08 + (intensity * 0.18)})`);
      gradient.addColorStop(1, `rgba(${red}, ${green}, ${blue}, 0)`);

      context.fillStyle = gradient;
      context.beginPath();
      context.arc(0, 0, 1, 0, Math.PI * 2);
      context.fill();
      context.restore();
    });

    return undefined;
  }, [heatmap, width, height]);

  return <canvas aria-hidden="true" className={styles.heatCanvas} ref={canvasRef} />;
}

function getSurfaceSize(surfaceNode, contentNode) {
  if (!surfaceNode || !contentNode) {
    return { height: 0, width: 0 };
  }

  return {
    height: Math.ceil(Math.max(contentNode.offsetHeight, contentNode.scrollHeight, contentNode.getBoundingClientRect().height)),
    width: Math.ceil(surfaceNode.getBoundingClientRect().width)
  };
}

function shouldBlockClick(target) {
  return Boolean(typeof target?.closest === 'function' && target.closest('a, button, input, textarea, select, summary'));
}

function HeatmapViewer() {
  const { dashboard, loading, error } = useAnalyticsDashboard(30);
  const surfaceRef = useRef(null);
  const contentRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const hasInitializedSelectionRef = useRef(false);
  const heatmaps = dashboard?.heatmaps?.home;
  const availableDevices = getAvailableDevices(heatmaps);
  const initialDevice = availableDevices[0] || 'desktop';
  const [activeDevice, setActiveDevice] = useState(initialDevice);
  const [activeMode, setActiveMode] = useState(getPreferredMode(heatmaps?.[initialDevice]));
  const [surfaceSize, setSurfaceSize] = useState({ height: 0, width: 0 });
  const isViewerReady = !loading && !error && Boolean(heatmaps) && availableDevices.length > 0;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useLayoutEffect(() => {
    if (!isViewerReady) {
      return undefined;
    }

    const surfaceNode = surfaceRef.current;
    const contentNode = contentRef.current;

    if (!surfaceNode || !contentNode || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const frameIds = [];
    const syncSurface = () => {
      const frameId = window.requestAnimationFrame(() => {
        const nextSize = getSurfaceSize(surfaceNode, contentNode);

        setSurfaceSize((currentSize) => (
          currentSize.width === nextSize.width && currentSize.height === nextSize.height
            ? currentSize
            : nextSize
        ));
      });

      frameIds.push(frameId);
    };

    const settleSurface = (framesLeft = 10) => {
      syncSurface();

      if (framesLeft > 0) {
        const frameId = window.requestAnimationFrame(() => settleSurface(framesLeft - 1));
        frameIds.push(frameId);
      }
    };

    settleSurface();
    resizeObserverRef.current = new ResizeObserver(syncSurface);
    resizeObserverRef.current.observe(surfaceNode);
    resizeObserverRef.current.observe(contentNode);
    window.addEventListener('resize', syncSurface);
    window.addEventListener('load', syncSurface);

    if (document.fonts?.ready) {
      document.fonts.ready.then(syncSurface).catch(() => {});
    }

    return () => {
      frameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      window.removeEventListener('resize', syncSurface);
      window.removeEventListener('load', syncSurface);
    };
  }, [activeDevice, isViewerReady]);

  useEffect(() => {
    if (hasInitializedSelectionRef.current || availableDevices.length === 0) {
      return;
    }

    const nextDevice = availableDevices[0];
    hasInitializedSelectionRef.current = true;
    setActiveDevice(nextDevice);
    setActiveMode(getPreferredMode(heatmaps?.[nextDevice]));
  }, [availableDevices, heatmaps]);

  useEffect(() => {
    if (!availableDevices.includes(activeDevice)) {
      const nextDevice = availableDevices[0] || 'desktop';
      setActiveDevice(nextDevice);
      setActiveMode(getPreferredMode(heatmaps?.[nextDevice]));
      return;
    }

    const activeHeatmapGroup = heatmaps?.[activeDevice];
    const availableModes = ['mouse', 'touch', 'attention'].filter((modeKey) => (
      (activeHeatmapGroup?.[modeKey]?.totalSamples || 0) > 0
    ));

    if (!availableModes.includes(activeMode)) {
      setActiveMode(getPreferredMode(activeHeatmapGroup));
    }
  }, [activeDevice, activeMode, availableDevices, heatmaps]);

  if (loading) {
    return <Loading label="Chargement de la visionneuse heatmap..." />;
  }

  if (error || !heatmaps || availableDevices.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>La visionneuse heatmap n’a pas pu être chargée.</p>
      </div>
    );
  }

  const activeHeatmapGroup = heatmaps[activeDevice];
  const activeHeatmap = activeHeatmapGroup?.[activeMode] || activeHeatmapGroup?.attention;
  const availableModes = ['mouse', 'touch', 'attention'].filter((modeKey) => (
    (activeHeatmapGroup?.[modeKey]?.totalSamples || 0) > 0
  ));
  const surfaceClassName = activeDevice === 'mobile'
    ? `${styles.siteSurface} ${styles.siteSurfaceMobile}`
    : `${styles.siteSurface} ${styles.siteSurfaceDesktop}`;

  return (
    <div className={styles.page}>
      <div className={styles.chrome}>
        <div className={styles.chromeSection}>
          <span className={styles.kicker}>Heatmap Viewer</span>
          <h1 className={styles.title}>Lecture plein écran du site.</h1>
        </div>

        <div className={styles.chromeSection}>
          <div className={styles.deviceTabs}>
            {availableDevices.map((deviceKey) => (
              <button
                className={activeDevice === deviceKey ? `${styles.deviceTab} ${styles.deviceTabActive}` : styles.deviceTab}
                key={deviceKey}
                onClick={() => {
                  setActiveDevice(deviceKey);
                  setActiveMode(getPreferredMode(heatmaps?.[deviceKey]));
                  window.scrollTo({ top: 0, behavior: 'auto' });
                }}
                type="button"
              >
                {DEVICE_PRESETS[deviceKey]?.label || deviceKey}
              </button>
            ))}
          </div>

          <div className={styles.modeTabs}>
            {availableModes.map((modeKey) => (
              <button
                className={activeMode === modeKey ? `${styles.modeTab} ${styles.modeTabActive}` : styles.modeTab}
                key={modeKey}
                onClick={() => setActiveMode(modeKey)}
                type="button"
              >
                {getModeLabel(modeKey)}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.chromeSection}>
          <div className={styles.meta}>
            <span>{DEVICE_PRESETS[activeDevice]?.label || activeDevice}</span>
            <span>
              {Number.isInteger(activeHeatmapGroup?.viewport?.width) ? activeHeatmapGroup.viewport.width : '?'}
              {' x '}
              {Number.isInteger(activeHeatmapGroup?.viewport?.height) ? activeHeatmapGroup.viewport.height : '?'}
            </span>
            <span>{activeHeatmap?.qualifiedSessions || 0} sessions qualifiées</span>
          </div>

          <button className={styles.backButton} onClick={() => { window.location.hash = '/analytics'; }} type="button">
            Retour analytics
          </button>
        </div>
      </div>

      <div className={styles.siteShell}>
        <div
          className={surfaceClassName}
          onClickCapture={(event) => {
            if (shouldBlockClick(event.target)) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          ref={surfaceRef}
        >
          <div className={styles.siteRender} ref={contentRef}>
            <Home heatmapRenderMode previewDevice={activeDevice} />
          </div>

          <div className={styles.heatOverlay}>
            <HeatCanvas
              heatmap={activeHeatmap}
              height={surfaceSize.height}
              viewport={activeHeatmapGroup?.viewport}
              width={surfaceSize.width}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeatmapViewer;
